import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import * as turf from "@turf/turf";
import { Box } from "@mui/material";
import MapZoomControls from "./MapZoomControl";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY || "";

const MML_ATTRIBUTION = "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0";
const MML_TILE = (layer: string) =>
  `https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/${layer}/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb`;

interface PropertyMiniMapProps {
  propertyGeometries?: any[];
  roads?: any[];
  height?: string;
}

const parcelGeom = (feature: any) => feature?.geom ?? feature?.geometry;

const parcelCenter = (feature: any): [number, number] | null => {
  const coords =
    feature?.location?.coordinates ??
    feature?.properties?.kiinteistotunnuksenSijainti?.coordinates ??
    feature?.geometry?.coordinates?.[0]?.[0];
  return Array.isArray(coords) && coords.length === 2
    ? [coords[0], coords[1]]
    : null;
};

const setGeojson = (map: mapboxgl.Map, id: string, geometry: any) => {
  const data: any = { type: "Feature", properties: {}, geometry };
  const source = map.getSource(id) as mapboxgl.GeoJSONSource | undefined;
  if (source) {
    source.setData(data);
    return false; // layers already exist
  }
  map.addSource(id, { type: "geojson", data });
  return true;
};

/**
 * Read-only mini map for the property view/yksiköinti tabs: renders the
 * parcel polygon(s) plus the association's roads (blue line + red start
 * point), matching the /map styling.
 *
 * The map is created exactly once per mount and layer effects key off a
 * `mapReady` flag, so parcel/road data may arrive in any order — this
 * replaces MapComponent's racy `propertyGeometries` mode, which could end
 * up drawing on an orphaned map instance.
 */
const PropertyMiniMap: React.FC<PropertyMiniMapProps> = ({
  propertyGeometries,
  roads,
  height = "70vh",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const geometriesRef = useRef(propertyGeometries);
  geometriesRef.current = propertyGeometries;

  // Create the map once per mount, remove it on unmount.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const center = parcelCenter(geometriesRef.current?.[0]) ?? [25.5, 62.5];
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/outdoors-v11",
      center,
      zoom: parcelCenter(geometriesRef.current?.[0]) ? 13 : 5,
    });
    mapRef.current = map;

    map.on("load", () => {
      for (const layer of [
        "mml-taustakartta",
        "mml-ortokuva",
        "mml-maastokartta",
      ]) {
        map.addSource(layer, {
          type: "raster",
          tiles: [MML_TILE(layer)],
          tileSize: 256,
          attribution: MML_ATTRIBUTION,
        });
        map.addLayer({
          id: layer,
          type: "raster",
          source: layer,
          paint: { "raster-opacity": 1 },
          layout: { visibility: "none" }, // Mapbox style is the default basemap
        });
      }
      setMapReady(true);
    });

    // Keep the canvas in sync with container size changes (e.g. the
    // Laajenna/Pienennä toggle on the yksiköinti tab).
    const observer = new ResizeObserver(() => map.resize());
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Parcel polygon(s) + location dot — tolerant of late-arriving data.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !Array.isArray(propertyGeometries)) return;

    propertyGeometries.forEach((feature, i) => {
      if (feature?.location) {
        const added = setGeojson(map, `location${i}`, feature.location);
        if (added) {
          map.addLayer({
            id: `location${i}`,
            type: "circle",
            source: `location${i}`,
            paint: { "circle-color": "red" },
          });
        }
      }

      const geom = parcelGeom(feature);
      if (!geom) return;
      const added = setGeojson(map, `property${i}`, geom);
      if (added) {
        map.addLayer({
          id: `property-fill${i}`,
          type: "fill",
          source: `property${i}`,
          paint: { "fill-color": "black", "fill-opacity": 0.3 },
        });
        map.addLayer({
          id: `property${i}`,
          type: "line",
          source: `property${i}`,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "blue", "line-width": 2 },
        });
      }
    });
  }, [mapReady, propertyGeometries]);

  // Association roads: blue line + red starting point, as on /map.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !Array.isArray(roads)) return;

    roads.forEach((road: any, i: number) => {
      if (road?.geom) {
        const added = setGeojson(map, `pg-route${i}`, road.geom);
        if (added) {
          map.addLayer({
            id: `pg-route${i}`,
            type: "line",
            source: `pg-route${i}`,
            layout: { "line-join": "round", "line-cap": "round" },
            paint: { "line-color": "blue", "line-width": 2 },
          });
        }
      }
      if (road?.location) {
        const added = setGeojson(map, `pg-road-start${i}`, road.location);
        if (added) {
          map.addLayer({
            id: `pg-road-start${i}`,
            type: "circle",
            source: `pg-road-start${i}`,
            paint: { "circle-color": "red" },
          });
        }
      }
    });
  }, [mapReady, roads]);

  // Frame the parcel once both the map and the geometry exist.
  useEffect(() => {
    const map = mapRef.current;
    const geom = parcelGeom(propertyGeometries?.[0]);
    if (!mapReady || !map || !geom) return;

    const bounds = turf.bbox(geom);
    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      { padding: 50, maxZoom: 14, duration: 800 }
    );
  }, [mapReady, propertyGeometries]);

  return (
    <Box sx={{ position: "relative", height, minHeight: 200 }}>
      <div
        ref={containerRef}
        className="map-container"
        style={{ position: "absolute", inset: 0 }}
      />
      <MapZoomControls
        onZoomIn={() => mapRef.current?.zoomIn()}
        onZoomOut={() => mapRef.current?.zoomOut()}
      />
    </Box>
  );
};

export default PropertyMiniMap;
